<template>
  <f-box bleed neutral class="md:p-24 mb-32">
    <h3 class="antialiased mb-16">Lånekalkulator</h3>
    <f-form>
      <f-expandable v-model="product.onCar" animated>
        <f-toggle v-model="carType" class="pb-24" radio-button equal-width small :toggles="carRadios" />
      </f-expandable>
      <div class="space-y-24">
        <f-money v-model="inputs.initialPrice" required label="Hvor mye vil du kjøpe for?" :hint="purchasePriceHint" @keypress.enter="clickCTA" :rules="hasValidPrice" />
        <f-money v-model="inputs.initialDownPayment" :required="hasAnyNumber" label="Hvor mye av sparepengene dine vil du bruke?" :hint="downPaymentHint" @keypress.enter="clickCTA" :rules="hasValidDownPayment" />
        <f-input v-model="inputs.initialPostalCode" label="Postnummeret ditt (valgfritt)" hint="f.eks 0176" :mask="postalMask" @keypress.enter="clickCTA" :rules="optionalPostalCode" />
        <f-field #control="{ form }">
          <f-button primary class="min-w-full justify-center md:min-w-max antialiased" style="color: white" ref="cta" @click="e => check(form, e)" :href="toSpeedcalc">Se tilbud fra flere banker</f-button>
        </f-field>
        <calc-form-controller :product="product" :inputs="inputs" />
      </div>
    </f-form>
  </f-box>
</template>

<script>
import { ref, computed, watch, reactive } from 'vue'
import fMoney from './fMoney.vue'
import { isNumber, toMoney, LTVratio } from '@finn-no/finance-lib'
import { fForm, fField, defaultInvalid, useForm } from '@fabric-ds/vue'
import { config } from '@nmp-js/huck/client'
import { useRoute } from 'vue-router'

const englishMap = {
  bil: 'car',
  campingvogn: 'caravan'
}
const carRadios = [{ label: 'Bil', value: 'bil'}, { label: 'Bobil/Campingvogn', value: 'campingvogn' }]
const postalMask = { numericOnly: true, blocks: [4] }
const forExample = 'f.eks. '

const exists = v => v.toString().match(/\d/)
const createParams = obj => {
  const params = new URLSearchParams()
  Object.entries(obj).forEach(([k, v]) => {
    if (exists(v)) params.append(k, v)
  })
  return params.toString()
}

const CalcFormController = {
  props: { product: Object, inputs: Object },
  setup(props) {
    const form = useForm()
    watch(props.product, () => {
      props.inputs.initialPrice = ''
      props.inputs.initialDownPayment = ''
      form.childrenShouldValidate = false
    })
    return () => null
  }
}

export default {
  name: 'CalcPanel',
  components: { fMoney, fForm, fField, CalcFormController },
  props: {
    product: Object
  },
  setup(props) {
    const route = useRoute()
    const inputs = reactive({
      initialPrice: '',
      initialDownPayment: '',
      initialPostalCode: '',
    })
    const carType = ref('bil')
    const cta = ref(null)
    const clickCTA = () => cta.value.$el.click()
    const purchasePriceHint = computed(() => forExample + (props.product.onHome ? toMoney(2_500_000) : toMoney(150_000)))
    const downPaymentHint = computed(() => forExample + (props.product.onHome ? toMoney(375_000) : toMoney(25_000)))
    const speedcalcParams = computed(() => createParams(inputs))
    const productParam = computed(() => props.product.onCar ? englishMap[carType.value] : route.name)
    const toSpeedcalc = computed(() => `/finance/marketplace/loan/${productParam.value}/?hasInitialValues=true&${speedcalcParams.value}`)
    const loanToValueRatio = computed(() => LTVratio({ loanAmount: (inputs.initialPrice - inputs.initialDownPayment), purchasePrice: inputs.initialPrice }))
    const minimumLTV = computed(() => props.product.onHome ? 0.85 : 1)
    const minimumPrice = computed(() => props.product.onHome ? 1000000 : 20000)
    const minimumDownPayment = computed(() => inputs.initialPrice * (1 - minimumLTV.value))
    const hasAnyNumber = e => isNumber(e) || defaultInvalid
    const hasValidPrice = computed(() => [v => v >= minimumPrice.value || { valid: false, hint: `Kjøpesummen må være minst ${toMoney(minimumPrice.value)}` }])
    const hasValidDownPayment = computed(() => [
      () => (isNumber(loanToValueRatio.value) && loanToValueRatio.value > 0) || { valid: false, hint: 'Ser ut som du ikke trenger å låne noe' },
      () => loanToValueRatio.value <= minimumLTV.value || { valid: false, hint: `Du må ha minst ${toMoney(minimumDownPayment.value)} for å kjøpe en bolig til den prisen` }
    ])

    const optionalPostalCode = [v => {
      if (v.length == 0) return true
      return (v.length == 4 && config.postalCodes.includes(v)) || { valid: false, hint: 'Ugyldig postnummer'}
    }]

    const check = (form, e) => {
      form.validate()
      if (!form.valid) return e.preventDefault()
      return e
    }

    return {
      inputs,
      postalMask,
      carType,
      cta,
      clickCTA,
      purchasePriceHint,
      downPaymentHint,
      check,
      toSpeedcalc,
      optionalPostalCode,
      carRadios,
      hasAnyNumber,
      hasValidDownPayment,
      hasValidPrice,
    }
  }
}
</script>
